import React, { useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Collapse,
  Text,
  useColorModeValue,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaUser, FaIdCard, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';

const VehicleClients = ({ vehicleCustomerHistories }) => {
  const [expandedClientId, setExpandedClientId] = useState(null);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const iconColor = useColorModeValue('blue.500', 'blue.300');

  const InfoField = ({ label, value, icon }) => (
    <Box 
      p={3} 
      bg={cardBg} 
      borderRadius="md" 
      border="1px solid" 
      borderColor={borderColor}
    >
      <HStack spacing={2} align="start">
        {icon && (
          <Icon 
            as={icon} 
            color={iconColor} 
            boxSize={4}
            mt={1}
          />
        )}
        <VStack align="start" spacing={1} flex={1}>
          <Text 
            fontSize="xs" 
            fontWeight="semibold" 
            color={labelColor} 
            textTransform="uppercase"
          >
            {label}
          </Text>
          <Text 
            fontSize="sm" 
            color={valueColor} 
            fontWeight="medium"
          >
            {value || 'N/A'}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );

  const toggleClientDetails = (id) => {
    setExpandedClientId((prev) => (prev === id ? null : id));
  };

  const isCurrentClient = (endDate) => !endDate || endDate === 'Presente';

  return (
    <Box mt={4}>
      <HStack mb={4} align="center">
        <Text fontSize="lg" fontWeight="bold" color={valueColor}>
          Historial de Clientes
        </Text>
        <Divider />
      </HStack>

      {vehicleCustomerHistories && vehicleCustomerHistories.length > 0 ? (
        vehicleCustomerHistories.map((history) => (
          <Box key={`${history.vehicleId}-${history.customerId}`} mb={4}>
            <Box
              p={4}
              bg={cardBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              cursor="pointer"
              onClick={() => toggleClientDetails(history.customerId)}
            >
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text fontWeight="bold" fontSize="md">
                      {history.customer?.name}
                    </Text>
                    {isCurrentClient(history.endDate) && (
                      <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="md">
                        Cliente Actual
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="sm" color={labelColor}>
                    Doc:{' '}
                    {history.customer?.document ||
                      history.customer?.documentNumber ||
                      'Sin documento'}
                  </Text>
                  <HStack spacing={4}>
                    <Text fontSize="xs" color={labelColor}>
                      Desde: {history.startDate ? 
                        new Date(history.startDate).toLocaleDateString('es-ES') : 'N/A'}
                    </Text>
                    {!isCurrentClient(history.endDate) && (
                      <Text fontSize="xs" color={labelColor}>
                        Hasta: {new Date(history.endDate).toLocaleDateString('es-ES')}
                      </Text>
                    )}
                  </HStack>
                </VStack>
                <IconButton
                  aria-label={
                    expandedClientId === history.customerId
                      ? 'Ocultar detalles'
                      : 'Mostrar detalles'
                  }
                  icon={
                    expandedClientId === history.customerId ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )
                  }
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleClientDetails(history.customerId);
                  }}
                />
              </Flex>
            </Box>

            <Collapse in={expandedClientId === history.customerId}>
              <Box mt={3} p={4} bg={bgColor} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                  Información del Cliente
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                  <InfoField 
                    label="Nombre Completo" 
                    value={history.customer?.name} 
                    icon={FaUser}
                  />
                  <InfoField 
                    label="Documento" 
                    value={
                      history.customer?.document ||
                      history.customer?.documentNumber
                    } 
                    icon={FaIdCard}
                  />
                  <InfoField 
                    label="Teléfono" 
                    value={
                      history.customer?.mobilePhone ||
                      history.customer?.phone
                    } 
                    icon={FaPhone}
                  />
                  <InfoField 
                    label="Correo Electrónico" 
                    value={history.customer?.email} 
                    icon={FaEnvelope}
                  />
                  <InfoField 
                    label="Dirección" 
                    value={history.customer?.address} 
                    icon={FaMapMarkerAlt}
                  />
                  <InfoField 
                    label="Período de Propiedad" 
                    value={`${history.startDate ? 
                      new Date(history.startDate).toLocaleDateString('es-ES') : 'N/A'} - ${
                      isCurrentClient(history.endDate) ? 'Presente' : 
                      new Date(history.endDate).toLocaleDateString('es-ES')}`} 
                    icon={FaCalendarAlt}
                  />
                </SimpleGrid>
              </Box>
            </Collapse>
          </Box>
        ))
      ) : (
        <Text color={labelColor}>No hay historial de clientes disponible.</Text>
      )}
    </Box>
  );
};

export default VehicleClients;